'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';

import { MCP_ENDPOINT, MCP_FINAL_CTA } from '~/lib/mcp-content';

/**
 * §8 Closing CTA. Dark ink card with a radial teal glow, mirroring the home
 * final CTA. The endpoint returns on a clean code line; the button scrolls
 * back up to the connect steps.
 */
export function McpFinalCta(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;

  const scrollToConnect = React.useCallback(() => {
    const el = document.getElementById('connect');
    if (el) {
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }
  }, [reduced]);

  return (
    <section className="relative bg-[color:var(--paper)] py-16 md:py-24">
      <div className="w-full relative mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <motion.div
          className="relative overflow-hidden rounded-[28px] bg-[#0A0B0A] text-[#F4F4F0] px-6 py-14 md:px-12 md:py-20"
          initial={{ opacity: 0, y: reduced ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(60% 80% at 50% 100%, color-mix(in srgb, #00C896 55%, transparent), transparent 70%)',
              opacity: 0.9,
            }}
          />

          <div className="relative mx-auto max-w-[680px] text-center">
            <h2 className="display-h2 text-3xl md:text-4xl lg:text-5xl mb-4 [text-wrap:balance]">
              <span style={{ color: '#B7BBB4' }}>{MCP_FINAL_CTA.headlineLead}</span>{' '}
              <span style={{ color: '#F4F4F0' }}>{MCP_FINAL_CTA.headlineRest}</span>
            </h2>

            <p className="mx-auto mb-8 max-w-[480px] text-lg text-[#B7BBB4] leading-relaxed">
              {MCP_FINAL_CTA.description}
            </p>

            <div className="mx-auto mb-8 max-w-[440px] rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <code className="font-mono text-[13px] md:text-[14px] text-[#E8F8F1]">
                {MCP_ENDPOINT}
              </code>
            </div>

            <div className="flex items-center justify-center">
              <Button
                size="lg"
                onClick={scrollToConnect}
                className="h-[52px] rounded-full bg-teal-500 px-7 text-[15.5px] font-medium text-[#001A12] shadow-md shadow-teal-900/20 transition-all hover:-translate-y-0.5 hover:bg-[#00E5AA] active:scale-[0.98]"
              >
                {MCP_FINAL_CTA.buttonText}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>

            <p className="mt-6 text-sm text-[#7E827C]">{MCP_FINAL_CTA.trustLine}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
